import AgoraRTC from 'agora-rtc-sdk-ng'

AgoraRTC.enableLogUpload()
AgoraRTC.setLogLevel(0)

/**
 * @type {Record<string, AgoraRtcHelper>}
 */
const AgoraRtcHelperMap = {}

/**
 * 封装了AgoraRTC的一些方法
 */
class AgoraRtcHelper {
  /**
   * @type {string}
   */
  clientKey

  appId
  channel
  token
  uid
  client

  /**
   * @type {import('agora-rtc-sdk-ng').IMicrophoneAudioTrack}
   */
  localAudioTrack
  /**
   * @type {import('agora-rtc-sdk-ng').ICameraVideoTrack}
   */
  localVideoTrack

  /**
   * @param {object} options
   * @param {string} options.appId
   * @param {string} options.channel
   * @param {string} options.token
   * @param {string} options.uid
   * @param {boolean} options.isHost
   */
  constructor(options) {
    if (!options.appId || !options.channel || !options.token || !options.uid) {
      throw new Error(
        'AgoraRtcHelper: options must have appId, channel, token, uid'
      )
    }
    if (options.isHost === undefined) {
      options.isHost = true
    }

    this.appId = options.appId
    this.channel = options.channel
    this.token = options.token
    this.uid = options.uid
    this.client = AgoraRTC.createClient({
      mode: 'live',
      codec: 'h264',
      role: options.isHost ? 'host' : 'audience'
    })
    this.listenUserPublished()
  }

  async join() {
    await this.client.join(this.appId, this.channel, this.token, this.uid)
  }

  async leave() {
    await this.client.unpublish(
      this.client.localTracks.filter((track) => track.isPublished)
    )
    this.client.localTracks.forEach((track) => {
      track.setEnabled(false)
      track.stop()
      track.close()
    })

    await this.client.leave()
  }

  async destroy() {
    this.client.removeAllListeners()
    if (this.remoteVideoElementId) {
      const remoteVideoElement = document.getElementById(
        this.remoteVideoElementId
      )
      if (remoteVideoElement) {
        remoteVideoElement.innerHTML = ''
      }
    }

    await this.leave()
    if (this.clientKey) {
      delete AgoraRtcHelperMap[this.clientKey]
    }
  }

  /**
   *
   * @param {object} options
   * @param {string?} options.cameraId
   */
  async pulish(options) {
    let configs
    if (options.cameraId) {
      configs = {
        cameraId: options.cameraId,
        encoderConfig: {
          width: 640,
          height: 480,
          bitrate: 500,
          frameRate: { ideal: 15 }
        }
      }
    } else {
      configs = {
        encoderConfig: {
          width: 640,
          height: 480,
          bitrate: 500,
          frameRate: { ideal: 15 }
        }
      }
    }

    const [localAudioTrack, localVideoTrack] =
      await AgoraRTC.createMicrophoneAndCameraTracks(undefined, configs)
    this.localAudioTrack = localAudioTrack
    this.localVideoTrack = localVideoTrack
    console.log('prapare to publish')
    await this.client.publish([localAudioTrack, localVideoTrack])
  }

  /**
   * 播放本地音频
   * @param {string} elementId
   * @param {string} deviceId
   */
  playLocalVideo(elementId) {
    if (!this.localVideoTrack) {
      throw new Error('AgoraRtcHelper: localVideoTrack is not created')
    }

    this.localVideoTrack._player = undefined
    const videoElement = document.getElementById(elementId)
    if (!videoElement) {
      throw new Error(`AgoraRtcHelper: element ${elementId} not found`)
    }

    videoElement.innerHTML = ''

    this.localVideoTrack.play(elementId)
  }

  /**
   * 切换摄像头
   * @param {string} deviceId
   */
  async switchVideoDevice(deviceId) {
    if (!this.localVideoTrack) {
      throw new Error('AgoraRtcHelper: localVideoTrack is not created')
    }
    await this.localVideoTrack.setDevice(deviceId)
  }

  /**
   * @type {string} 用来播放远端视频的元素id
   */
  remoteVideoElementId

  /**
   * 这个函数不管远端用户是谁，只要有视频流就播放
   * @param {string} elementId
   */
  async playRemoteVideo(elementId) {
    this.remoteVideoElementId = elementId
  }

  listenUserPublished() {
    this.client.on('user-published', async (user, mediaType) => {
      if (!this.remoteVideoElementId) {
        return
      }
      await this.client.subscribe(user, mediaType)
      if (mediaType === 'video') {
        const remoteVideoTrack = user.videoTrack
        if (!remoteVideoTrack) {
          return
        }
        const videoElement = document.getElementById(this.remoteVideoElementId)
        if (!videoElement) {
          return
        }
        videoElement.innerHTML = ''

        remoteVideoTrack.play(this.remoteVideoElementId)
      }
    })
  }

  /**
   *
   * @param {(user: import('agora-rtc-sdk-ng').IUserInfo, mediaType: 'video' | 'audio') => void} cb
   */
  listenVideoPublish(cb) {
    const cbWrapper = (user, mediaType) => {
      if (mediaType === 'video') {
        cb(user, mediaType)
      }
    }
    this.client.on('user-published', cbWrapper)
    return () => {
      this.client.off('user-published', cbWrapper)
    }
  }

  /**
   *
   * @param {(user: import('agora-rtc-sdk-ng').IUserInfo, mediaType: 'video' | 'audio') => void} cb
   */
  listenVideoUnpublish(cb) {
    const cbWrapper = (user, mediaType) => {
      if (mediaType === 'video') {
        cb(user, mediaType)
      }
    }
    this.client.on('user-unpublished', cbWrapper)
    return () => {
      this.client.off('user-unpublished', cbWrapper)
    }
  }

  /**
   *
   * @param {() => void} cb
   */
  listenTokenWillExpire(cb) {
    this.client.on('token-privilege-will-expire', cb)
    return () => {
      this.client.off('token-privilege-will-expire', cb)
    }
  }

  /**
   *
   * @param {() => void} cb
   */
  listenTokenExpired(cb) {
    this.client.on('token-privilege-did-expire', cb)
    return () => {
      this.client.off('token-privilege-did-expire', cb)
    }
  }

  hasRemoteVideo() {
    const remoteUsers = this.client.remoteUsers
    return remoteUsers.some((user) => user.hasVideo)
  }

  /**
   *
   * @param {string} newToken
   */
  async renewToken(newToken) {
    await this.client.renewToken(newToken)
    this.token = newToken
  }
}
export default class AgoraRtcTool {
  static async getDevices({ audio = true, video = true }) {
    const deveces = {}
    if (audio) {
      deveces.microphones = await AgoraRTC.getMicrophones()
    }
    if (video) {
      deveces.cameras = await AgoraRTC.getCameras()
    }
    return deveces
  }

  /**
   * @param {string} key
   * @param {object} options
   * @param {string} options.appId
   * @param {string} options.channel
   * @param {string} options.token
   * @param {string} options.uid
   * @param {boolean} options.isHost
   */
  static initClient(key, options) {
    const client = new AgoraRtcHelper(options)
    AgoraRtcHelperMap[key] = client
    client.clientKey = key
    return client
  }

  /**
   * @param {string} key
   */
  static getClient(key) {
    return AgoraRtcHelperMap[key]
  }
}
